// Modelos: los proveedores conectados (OpenAI, Anthropic, Gemini, uno propio…) y los nombres que usan las llaves.
// Un modelo del gateway es un nombre estable con una lista de destinos en orden: si el primero falla por algo
// pasajero, responde el siguiente. Cambiar de proveedor no obliga a tocar ningún agente.
// Los modelos de Glubi («glubi/…») los da el equipo de Glubi con su propia clave y un tope al mes para este negocio:
// se ven acá y se usan como cualquier otro, pero no se cambian desde la consola.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, Tag, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { usd } from '../lib/formato';
import { PROVEEDORES, type PlantillaProveedor } from '../lib/plantillas';
import { useSesion } from '../lib/sesion';
import { AvisoSoloVer, Estado, SoloSiAdministra } from '../componentes/Comunes';
import { credencialInicial, ElegirCredencial, resolverCredencial, type EstadoCredencial } from '../componentes/ElegirCredencial';

const TIPO: Record<string, string> = { openai: 'OpenAI', anthropic: 'Anthropic', compatible: 'Compatible con OpenAI' };

interface FormProveedor { id: string | null; nombre: string; tipo: PlantillaProveedor['tipo']; urlBase: string; estado: 'activo' | 'pausado'; credencial: EstadoCredencial }
interface Destino { proveedorId: string; modeloProveedor: string; precioEntrada: string; precioSalida: string }
interface FormModelo { id: string | null; nombre: string; descripcion: string; estado: 'activo' | 'pausado'; destinos: Destino[] }

export function Modelos() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const proveedores = useQuery({ queryKey: ['proveedores'], queryFn: () => api<any[]>('/api/proveedores') });
  const modelos = useQuery({ queryKey: ['modelos'], queryFn: () => api<any[]>('/api/modelos') });
  const [elegir, setElegir] = useState(false);
  const [prov, setProv] = useState<FormProveedor | null>(null);
  const [mod, setMod] = useState<FormModelo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disponibles, setDisponibles] = useState<Record<string, string[]>>({});

  const recargar = () => { void qc.invalidateQueries({ queryKey: ['proveedores'] }); void qc.invalidateQueries({ queryKey: ['modelos'] }); void qc.invalidateQueries({ queryKey: ['secretos'] }); };

  const guardarProveedor = useMutation({
    mutationFn: async (f: FormProveedor) => {
      const secretoId = await resolverCredencial(f.credencial);
      return api(f.id ? `/api/proveedores/${f.id}` : '/api/proveedores', { nombre: f.nombre, tipo: f.tipo, urlBase: f.urlBase, estado: f.estado, secretoId });
    },
    onSuccess: () => { recargar(); aviso('Proveedor guardado'); setProv(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const borrarProveedor = useMutation({
    mutationFn: (id: string) => api(`/api/proveedores/${id}/borrar`, {}),
    onSuccess: () => { recargar(); aviso('Proveedor quitado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const probar = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean; modelos?: string[]; error?: string }>(`/api/proveedores/${id}/modelos`, {}),
    onSuccess: (r, id) => {
      if (r.ok) { setDisponibles((d) => ({ ...d, [id]: r.modelos ?? [] })); aviso(`Conexión bien: ${r.modelos?.length ?? 0} modelos disponibles`); }
      else aviso(r.error ?? 'No respondió');
    },
  });
  const guardarModelo = useMutation({
    mutationFn: (f: FormModelo) => api(f.id ? `/api/modelos/${f.id}` : '/api/modelos', {
      nombre: f.nombre, descripcion: f.descripcion || null, estado: f.estado,
      destinos: f.destinos.map((d) => ({ proveedorId: d.proveedorId, modeloProveedor: d.modeloProveedor, precioEntrada: Number(d.precioEntrada || 0), precioSalida: Number(d.precioSalida || 0) })),
    }),
    onSuccess: () => { recargar(); aviso('Modelo guardado'); setMod(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const borrarModelo = useMutation({
    mutationFn: (id: string) => api(`/api/modelos/${id}/borrar`, {}),
    onSuccess: () => { recargar(); aviso('Modelo quitado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (proveedores.isLoading || modelos.isLoading) return <Cargando />;
  const listaProveedores = proveedores.data ?? [];
  const propios = (modelos.data ?? []).filter((m) => !m.deGlubi);
  const deGlubi = (modelos.data ?? []).filter((m) => m.deGlubi);
  const destinoVacio = (): Destino => ({ proveedorId: listaProveedores[0]?.id ?? '', modeloProveedor: '', precioEntrada: '', precioSalida: '' });

  const cambiarDestino = (i: number, cambio: Partial<Destino>) => setMod((m) => m && { ...m, destinos: m.destinos.map((d, j) => (j === i ? { ...d, ...cambio } : d)) });
  const mover = (i: number, paso: number) => setMod((m) => {
    if (!m) return m;
    const d = [...m.destinos];
    const [x] = d.splice(i, 1);
    d.splice(i + paso, 0, x!);
    return { ...m, destinos: d };
  });

  return (
    <div className="page">
      <PageHead title="Modelos" subtitle="Los proveedores de modelos de lenguaje y los nombres que usan las llaves. Cada modelo puede tener respaldos: si el primero falla, responde el siguiente.">
        <SoloSiAdministra>
          <Button variant="secondary" icon="plus" onClick={() => { setError(null); setElegir(true); }}>Conectar proveedor</Button>
          <Button icon="plus" disabled={!listaProveedores.length} onClick={() => { setError(null); setMod({ id: null, nombre: '', descripcion: '', estado: 'activo', destinos: [destinoVacio()] }); }}>Nuevo modelo</Button>
        </SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />

      {deGlubi.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Modelos de Glubi</div>
          <p className="muted" style={{ fontSize: 13, margin: '0 0 10px' }}>Los da Glubi, con su propia clave. Se eligen en los agentes, las llaves y el probador como cualquier otro, hasta el tope del mes.</p>
          <Tabla rowKey="id" rows={deGlubi}
            columns={[
              { key: 'nombre', label: 'Nombre para los agentes', render: (m: any) => <div><code style={{ fontSize: 13 }}>{m.nombre}</code>{m.descripcion && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{m.descripcion}</div>}</div> },
              { key: 'gasto', label: 'Este mes', render: (m: any) => <GastoContraTope gasto={m.gastoMes} tope={m.topeMensual} /> },
              { key: 'estado', label: 'Estado', render: (m: any) => <Estado estado={m.estado} /> },
            ]} />
          <div style={{ height: 22 }} />
        </>
      )}

      <div className="eyebrow" style={{ marginBottom: 8 }}>{deGlubi.length ? 'Modelos propios' : 'Modelos del gateway'}</div>
      {!propios.length ? (
        <Vacio titulo="Todavía no hay modelos">{listaProveedores.length ? 'Crear un modelo con el nombre que van a usar los agentes, por ejemplo «rapido» o «redactor».' : 'Primero conectar un proveedor.'}</Vacio>
      ) : (
        <Tabla rowKey="id" rows={propios}
          columns={[
            { key: 'nombre', label: 'Nombre para los agentes', render: (m: any) => <div><code style={{ fontSize: 13 }}>{m.nombre}</code>{m.descripcion && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{m.descripcion}</div>}</div> },
            { key: 'destinos', label: 'Va a (en orden)', render: (m: any) => (
              <div className="stack" style={{ gap: 4 }}>
                {m.destinos.map((d: any, i: number) => (
                  <div key={i} style={{ fontSize: 13 }}>
                    <span className="muted">{i === 0 ? '' : 'si falla → '}</span>{d.proveedor} · <code>{d.modeloProveedor}</code>
                    {d.estadoProveedor === 'pausado' && <> <Tag>pausado</Tag></>}
                  </div>
                ))}
              </div>
            ) },
            { key: 'precio', label: 'Precio por millón (entra / sale)', render: (m: any) => <span className="num" style={{ fontSize: 13 }}>{usd(m.destinos[0]?.precioEntrada)} / {usd(m.destinos[0]?.precioSalida)}</span> },
            { key: 'estado', label: 'Estado', render: (m: any) => <Estado estado={m.estado} /> },
            { key: 'acciones', label: '', render: (m: any) => (
              <SoloSiAdministra>
                <div className="fila acciones">
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => { setError(null); setMod({
                    id: m.id, nombre: m.nombre, descripcion: m.descripcion ?? '', estado: m.estado,
                    destinos: m.destinos.map((d: any) => ({ proveedorId: d.proveedorId, modeloProveedor: d.modeloProveedor, precioEntrada: String(d.precioEntrada ?? ''), precioSalida: String(d.precioSalida ?? '') })),
                  }); }}>Cambiar</Button>
                  <Button size="sm" variant="ghost" icon="trash" onClick={() => { if (confirm(`¿Quitar «${m.nombre}»? Las llaves que lo usan dejan de poder llamarlo.`)) borrarModelo.mutate(m.id); }}>Quitar</Button>
                </div>
              </SoloSiAdministra>
            ) },
          ]} />
      )}

      <div className="eyebrow" style={{ margin: '22px 0 8px' }}>Proveedores conectados</div>
      {!listaProveedores.length ? (
        <Vacio titulo="Ningún proveedor todavía" accion={<SoloSiAdministra><Button icon="plus" onClick={() => setElegir(true)}>Conectar proveedor</Button></SoloSiAdministra>}>
          OpenAI, Anthropic, Gemini, Groq, OpenRouter o un servidor propio. Su clave queda en la bóveda del negocio, no en cada agente.
        </Vacio>
      ) : (
        <Tabla rowKey="id" rows={listaProveedores}
          columns={[
            { key: 'nombre', label: 'Proveedor', render: (p: any) => <b style={{ fontWeight: 500 }}>{p.nombre}</b> },
            { key: 'tipo', label: 'Tipo', render: (p: any) => TIPO[p.tipo] },
            { key: 'url_base', label: 'Dirección', render: (p: any) => <code>{p.url_base}</code> },
            { key: 'secreto', label: 'Credencial', render: (p: any) => p.secreto ?? <span className="muted">Ninguna</span> },
            { key: 'modelos', label: 'Modelos', numeric: true },
            { key: 'estado', label: 'Estado', render: (p: any) => <Estado estado={p.estado} /> },
            { key: 'acciones', label: '', render: (p: any) => (
              <SoloSiAdministra>
                <div className="fila acciones">
                  <Button size="sm" variant="ghost" icon="signal" disabled={probar.isPending} onClick={() => probar.mutate(p.id)}>Probar</Button>
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => { setError(null); setProv({ id: p.id, nombre: p.nombre, tipo: p.tipo, urlBase: p.url_base, estado: p.estado, credencial: p.secreto_id ? credencialInicial(p.secreto_id) : { modo: 'ninguna' } }); }}>Cambiar</Button>
                  <Button size="sm" variant="ghost" icon="trash" disabled={p.modelos > 0} onClick={() => { if (confirm(`¿Quitar «${p.nombre}»?`)) borrarProveedor.mutate(p.id); }}>Quitar</Button>
                </div>
              </SoloSiAdministra>
            ) },
          ]} />
      )}

      {elegir && (
        <Modal title="Conectar un proveedor" subtitle="Elegir de dónde vienen los modelos. La dirección y la credencial se pueden cambiar después." onClose={() => setElegir(false)} width={720}>
          <div className="plantillas">
            {PROVEEDORES.map((p) => (
              <button key={p.clave} className="plantilla" onClick={() => {
                setElegir(false);
                setProv({ id: null, nombre: p.clave === 'otro' ? '' : p.titulo, tipo: p.tipo, urlBase: p.urlBase, estado: 'activo', credencial: p.sinCredencial ? { modo: 'ninguna' } : { modo: 'nueva', nombre: `Clave de ${p.titulo}`, valor: '' } });
              }}>
                <b>{p.titulo}</b><span>{p.nota}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {prov && (
        <Modal title={prov.id ? `Cambiar «${prov.nombre}»` : 'Conectar proveedor'} onClose={() => setProv(null)} width={620}
          footer={<>
            <Button variant="ghost" onClick={() => setProv(null)}>Cancelar</Button>
            <Button disabled={guardarProveedor.isPending || prov.nombre.trim().length < 2 || !prov.urlBase.startsWith('http')} onClick={() => { setError(null); guardarProveedor.mutate(prov); }}>Guardar</Button>
          </>}>
          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Nombre" value={prov.nombre} onChange={(e) => setProv({ ...prov, nombre: e.target.value })} />
            <Select label="Forma de la API" value={prov.tipo} onChange={(e) => setProv({ ...prov, tipo: e.target.value as FormProveedor['tipo'] })}
              options={[{ value: 'openai', label: 'OpenAI' }, { value: 'anthropic', label: 'Anthropic' }, { value: 'compatible', label: 'Compatible con OpenAI' }]} />
          </div>
          <Input label="Dirección base" value={prov.urlBase} onChange={(e) => setProv({ ...prov, urlBase: e.target.value })} />
          <ElegirCredencial valor={prov.credencial} onChange={(credencial) => setProv({ ...prov, credencial })} nombreSugerido={`Clave de ${prov.nombre}`} opcional />
          {prov.id && <Select label="Estado" value={prov.estado} onChange={(e) => setProv({ ...prov, estado: e.target.value as 'activo' | 'pausado' })} options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: sus modelos saltan al respaldo' }]} />}
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}

      {mod && (
        <Modal title={mod.id ? `Cambiar «${mod.nombre}»` : 'Nuevo modelo'} subtitle="Los agentes piden este nombre en «model». Los precios son en dólares por millón de tokens y sirven para el gasto y los topes." onClose={() => setMod(null)} width={760}
          footer={<>
            <Button variant="ghost" onClick={() => setMod(null)}>Cancelar</Button>
            <Button disabled={guardarModelo.isPending || !mod.nombre.trim() || mod.destinos.some((d) => !d.proveedorId || !d.modeloProveedor.trim())} onClick={() => { setError(null); guardarModelo.mutate(mod); }}>Guardar</Button>
          </>}>
          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Nombre para los agentes" value={mod.nombre} onChange={(e) => setMod({ ...mod, nombre: e.target.value })} placeholder="rapido" helper="Letras, números y . _ : / -" />
            <Input label="Para qué es (opcional)" value={mod.descripcion} onChange={(e) => setMod({ ...mod, descripcion: e.target.value })} />
          </div>
          <span className="etiqueta">Destinos, en orden</span>
          {mod.destinos.map((d, i) => (
            <div key={i} className="operacion">
              <div className="entre">
                <Badge state={i === 0 ? 'ok' : 'neutral'}>{i === 0 ? 'Principal' : `Respaldo ${i}`}</Badge>
                <div className="fila">
                  <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => mover(i, -1)}>Subir</Button>
                  <Button size="sm" variant="ghost" icon="trash" disabled={mod.destinos.length === 1} onClick={() => setMod({ ...mod, destinos: mod.destinos.filter((_, j) => j !== i) })}>Quitar</Button>
                </div>
              </div>
              <div className="grid-2" style={{ gap: 10 }}>
                <Select label="Proveedor" value={d.proveedorId} onChange={(e) => cambiarDestino(i, { proveedorId: e.target.value })}
                  options={listaProveedores.map((p) => ({ value: p.id as string, label: p.nombre as string }))} />
                <div>
                  <Input label="Modelo del proveedor" value={d.modeloProveedor} onChange={(e) => cambiarDestino(i, { modeloProveedor: e.target.value })}
                    helper={disponibles[d.proveedorId] ? `${disponibles[d.proveedorId]!.length} disponibles; empezar a escribir` : 'Con «Probar» en el proveedor se ve la lista'} />
                  {disponibles[d.proveedorId] && d.modeloProveedor.length > 0 && (
                    <div className="fila" style={{ marginTop: 6 }}>
                      {disponibles[d.proveedorId]!.filter((m) => m.includes(d.modeloProveedor) && m !== d.modeloProveedor).slice(0, 6).map((m) => (
                        <button key={m} className="chip" onClick={() => cambiarDestino(i, { modeloProveedor: m })}>{m}</button>
                      ))}
                    </div>
                  )}
                </div>
                <Input label="Precio de entrada" unit="US$ / M" inputMode="decimal" value={d.precioEntrada} onChange={(e) => cambiarDestino(i, { precioEntrada: e.target.value.replace(',', '.') })} />
                <Input label="Precio de salida" unit="US$ / M" inputMode="decimal" value={d.precioSalida} onChange={(e) => cambiarDestino(i, { precioSalida: e.target.value.replace(',', '.') })} />
              </div>
            </div>
          ))}
          {mod.destinos.length < 8 && <div><Button size="sm" variant="secondary" icon="plus" onClick={() => setMod({ ...mod, destinos: [...mod.destinos, destinoVacio()] })}>Agregar respaldo</Button></div>}
          {mod.id && <Select label="Estado" value={mod.estado} onChange={(e) => setMod({ ...mod, estado: e.target.value as 'activo' | 'pausado' })} options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: nadie lo puede usar' }]} />}
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}
    </div>
  );
}

// Lo gastado contra el tope que puso Glubi. Llegar al tope no rompe nada: el modelo responde 429 hasta el mes siguiente.
function GastoContraTope({ gasto, tope }: { gasto: number; tope: number }) {
  const pct = tope > 0 ? Math.min(100, (gasto / tope) * 100) : 100;
  const lleno = gasto >= tope;
  return (
    <div style={{ minWidth: 160 }}>
      <div className="num" style={{ fontSize: 13 }}>{usd(gasto)} <span className="muted">de {usd(tope)}</span></div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--surface-sunken)', marginTop: 4 }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: lleno ? 'var(--state-error)' : 'var(--amber)' }} />
      </div>
      {lleno && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Llegó al tope de este mes</div>}
    </div>
  );
}
